import { convertDate } from '@/helpers/track-order';
import { sendGTMEvent } from '@next/third-parties/google';
import { getCookie } from 'cookies-next';
import crypto from 'crypto-js';
import { getSession } from 'next-auth/react';
import {
  get_product_brand,
  get_product_category_sub_category,
  get_product_image,
  get_product_price,
  get_product_sku,
  getAuthPayload,
  getPurchaseStats,
  getCallbackReferralUrl,
  getCategoryClickPayload,
  getShippingTier,
  getPaymentType,
  getItemListId,
  markPurchasePushed,
  getGlobalVariables,
  getErrorContext,
  gtmEvent,
  wasPurchasePushed,
  gtmEventNow,
} from './helper';
import {
  getItemAttribution,
  storeItemListAttribution,
} from './item-attribution';
import { noteExplicitError, reportUnlessExplicit } from './error-suppression';
import { takeRecentApiFailure } from './api-failures';
import { buildListItem, getItemId } from './item-lists';
import { clean, round2 } from './format';

const getSharedSession = () => {
  if (typeof window === 'undefined') return null;
  return getSession();
};

// Event Search
export const event_search = ({
  search_term,
  search_results_count,
  search_type,
}) => {
  const term = search_term?.trim();
  if (!term) return;
  return gtmEvent(
    'search',
    {
      search_term: term,
      search_results_count: Number(search_results_count || 0),
      search_type,
    },
    null,
    false,
    false
  );
};

// Event Category Click
export const event_category_click = ({
  menuTreeData,
  link,
  category,
  click_location,
}) => {
  const payload = getCategoryClickPayload({
    menuTreeData,
    link,
    category,
    click_location,
  });
  if (!payload) return;
  return gtmEvent('category_click', payload, null, false, false);
};

// Event Global Variables
let last_global_variables = null;

export const event_global_variables = ({
  pathname,
  locale,
  session,
  shippingMethod,
  shippingAddress,
}) => {
  if (typeof window !== 'object') return;

  const payload = getGlobalVariables({
    pathname,
    locale,
    session,
    shippingMethod,
    shippingAddress,
  });
  const snapshot = `${pathname}|${JSON.stringify(payload)}`;

  if (snapshot === last_global_variables) return;
  last_global_variables = snapshot;

  return sendGTMEvent(payload);
};

// DAAM asked for `remove_from_wishlist` flat: `currency`, `value` and `items`
// at the top level, with no `ecommerce` object. GTM keeps top-level keys in its
// data model, so they are cleared straight after the push, once the event's
// tags have read them, and cannot leak into a later event. Deferred like
// `gtmEvent`.
const pushFlatEcommerceEvent = (event, data) => {
  if (typeof window !== 'object') return;
  setTimeout(() => {
    gtmEventNow(event, data, null, false, false);
    sendGTMEvent(
      Object.fromEntries(Object.keys(data).map((key) => [key, null]))
    );
  }, 500);
};

// Event Add To Wishlist
export const event_add_to_wishlist = ({
  product,
  index,
  locale,
  item_list_id,
  item_list_name,
}) => {
  const item_id = getItemId(product);
  const attribution = item_id ? getItemAttribution(item_id) : undefined;
  const item = buildListItem({
    product,
    index,
    locale,
    list: attribution?.item_list_id
      ? attribution
      : item_list_id && {
          item_list_id: String(item_list_id),
          ...(item_list_name && { item_list_name }),
        },
  });
  if (!item) return;
  return gtmEvent(
    'add_to_wishlist',
    {
      currency: 'SAR',
      value: item?.price,
      items: [item],
    },
    null,
    true,
    false
  );
};

// Event Remove From Wishlist
export const event_remove_from_wishlist = ({
  product,
  index,
  locale,
  item_list_id,
  item_list_name,
}) => {
  const item_id = getItemId(product);
  const attribution = item_id ? getItemAttribution(item_id) : undefined;
  const item = buildListItem({
    product,
    index,
    locale,
    list: attribution?.item_list_id
      ? attribution
      : item_list_id && {
          item_list_id: String(item_list_id),
          ...(item_list_name && { item_list_name }),
        },
  });
  if (!item) return;
  return pushFlatEcommerceEvent('remove_from_wishlist', {
    currency: 'SAR',
    value: item?.price,
    items: [item],
  });
};

// Event Refund
export const event_refund = (order, locale) => {
  return gtmEvent('refund', {
    transaction_id: order?.increment_id,
    value: order?.grand_total,
    currency: 'SAR',
    tax: order?.tax_amount,
    shipping: order?.shipping_amount,
    ...(order?.coupon_code && { coupon: order?.coupon_code }),
    items: buildCartItems({
      cartLines: order?.items,
      locale,
    }),
  });
};

export const event_cancel_order = ({ order_id, reason }) => {
  return gtmEvent(
    'order_cancelled',
    {
      order_id,
      reason,
    },
    null,
    true,
    false
  );
};

// Event Shipping Info
export const event_shipping_info = ({
  shippingAddress,
  shippingMethod,
  shippingValue,
}) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    'shipping_selected',
    {
      cart_id,
      ...(shippingAddress?.firstname && {
        name: shippingAddress?.firstname,
      }),
      ...(shippingAddress?.telephone && {
        phone: shippingAddress?.telephone,
      }),
      ...(shippingAddress?.email && {
        email: shippingAddress?.email,
      }),
      ...(shippingAddress?.city && {
        city: shippingAddress?.city,
      }),
      country: 'KSA',
      ...(shippingAddress?.postcode && {
        pincode: shippingAddress?.postcode,
      }),
      ...(shippingAddress?.street?.[0] && {
        address: shippingAddress?.street?.[0],
      }),
      shipping_method: shippingMethod,
      shipping_charges: shippingValue || 'free',
    },
    null,
    true,
    false
  );
};

// Event Payment Info
export const event_payment_info = ({
  cartData,
  payment_mode,
  shipping_method,
}) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    'payment_mode_selected',
    {
      cart_id,
      shipping_method,
      payment_mode,
      payment_type: payment_mode,
      source_event: 'add_payment_info',
      value: cartData?.grand_total,
      item_count: cartData?.items?.length,
      number_of_products: cartData?.items?.length,
      items: cartData?.items?.map((product) => {
        return {
          product_id: product?.entity_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
      currency: 'SAR',
      shipping_charges: cartData?.shipping_amount || 'free',
    },
    null,
    true
  );
};

// Payment Failure
export const event_payment_failed = ({
  payment_type,
  reason,
  transaction_id,
  value,
}) => {
  return gtmEvent(
    'payment_failed',
    {
      ...(payment_type && { payment_type: getPaymentType(payment_type) }),
      ...(reason && { reason }),
      ...(transaction_id && { transaction_id: String(transaction_id) }),
      ...(value !== undefined &&
        value !== null &&
        value !== '' && { value: Number(value) }),
    },
    null,
    false,
    false
  );
};

// Event View Item used in pdp
// Fired once per PDP view, as soon as the page has product data, and again on an
// SPA move to another PDP — see `useViewItem`, which holds the once-per-view
// guard.
//
// This carries none of the legacy payload's non-spec parameters (`source`,
// `view_type`, `stock_availability`). The legacy `product_viewed` still fires
// beside it from `useViewItem` — see the legacy block at the end of this file.
//
// Attribution is attached at item level when it exists: the list the product was
// chosen from (`item_list_id` / `item_list_name`, per item, 24h) and the
// promotion that brought the shopper in (`promotion_id` / `promotion_name`,
// 24h). A PDP opened directly has neither, and spec §2 drops the parameters
// rather than sending them empty.
//
// `value` equals the item's `price` — the spec's quantity is 1.
//
// Pushed through `gtmEventNow` rather than the 500 ms-deferred `gtmEvent`,
// because the instruction is to push immediately and, on a fast Related-product
// hop between PDPs, a deferred push could land after the next PDP had already
// rendered.
//
// `event_id` is in the spec sample but out of scope for this project.
export const event_view_item = ({ product, locale }) => {
  const item_id = getItemId(product);
  if (!item_id) return;

  const item = buildListItem({
    product,
    index: 0,
    list: getItemAttribution(item_id),
    locale,
  });
  if (!item) return;

  return gtmEventNow(
    'view_item',
    {
      currency: 'SAR',
      ...(item?.price !== undefined && { value: item.price }),
      items: [item],
    },
    null,
    true,
    false
  );
};

// Fired by every action that adds units to the cart: the Add To Cart button on
// any product card, the PDP button with its selected quantity, PDP Buy Now, and
// the + stepper on the cart page, in the mini-cart and on the PDP once the
// product is already in the cart.
//
// `quantity` is the **delta** — the units this action adds — not the resulting
// cart quantity, and `value` is `price × quantity`. The PDP's pre-add stepper
// only moves local state and fires nothing; its selected quantity arrives here
// once, on Add To Cart.
//
// The spec models a quantity increase as an ordinary `add_to_cart`, so this one
// event covers both. It carries none of the legacy payload (`cart_id`,
// `source`, `atc_type`, `total_product_price`, `total_cart_value`,
// `stock_availability`, `bundle_deal_applied`); the legacy
// `product_quantity_increased` still fires beside it from the cart stepper —
// see the legacy block at the end of this file.
//
// Attribution rides at item level as it does on `view_item`. `event_id` is in
// the spec sample but out of scope.
export const event_add_to_cart = ({ product, quantity = 1, locale, list }) => {
  const item_id = getItemId(product);
  if (!item_id) return;

  // A product card knows the list it sits in. Remember it here so this add —
  // and the removal, checkout and purchase that may follow — carry the origin
  // even when the shopper never opened the PDP and so never fired
  // `select_item`.
  if (list?.item_list_id) storeItemListAttribution({ item_id, ...list });

  const item = buildListItem({
    product,
    index: 0,
    list: getItemAttribution(item_id),
    locale,
    quantity,
  });
  if (!item) return;

  const units = Number(quantity) || 1;
  const value =
    item?.price !== undefined ? round2(item.price * units) : undefined;

  // Synchronous: Buy Now leaves for /checkout straight after this push.
  return gtmEventNow(
    'add_to_cart',
    {
      currency: 'SAR',
      ...(value !== undefined && { value }),
      items: [item],
    },
    null,
    true,
    false
  );
};

// Event Remove From Cart
// used in mini cart, cart popup, cart page, product card
// Fired by every action that takes units out of the cart: Delete on the cart
// page, in the mini-cart and in the cart pop-up, the trash button on a product
// card once the line is down to one, and the - stepper on the cart page, in the
// mini-cart and on the PDP.
//
// `quantity` is the **delta** — units this action removes — so a Delete reports
// the whole line and a stepper press reports 1. `value` is `price × quantity`.
//
// The spec models a decrease as an ordinary `remove_from_cart`. This carries
// none of the legacy payload (`cart_id`, `source`, `view_type`,
// `total_product_price`, `total_cart_value`, `stock_availability`,
// `bundle_deal_applied`); the legacy `product_quantity_decreased` still fires
// beside it from the cart stepper — see the legacy block at the end of this
// file.
//
// The list context is read from the same 24-hour store `add_to_cart` wrote to,
// so a removal reports the origin the add reported.
export const event_remove_from_cart = ({
  product,
  quantity,
  locale,
  unavailableSkus,
}) => {
  const item_id = getItemId(product);
  if (!item_id) return;

  const units = Number(quantity) || 1;
  const item = buildListItem({
    product,
    index: 0,
    list: getItemAttribution(item_id),
    locale,
    quantity: units,
    unavailableSkus,
  });
  if (!item) return;

  const value =
    item?.price !== undefined ? round2(item.price * units) : undefined;

  return gtmEventNow(
    'remove_from_cart',
    {
      currency: 'SAR',
      ...(value !== undefined && { value }),
      items: [item],
    },
    null,
    true,
    false
  );
};

export const event_buy_now = ({ product }) => {
  const cart_id = getCookie('quote');
  const is_stock =
    (product?.stock?.is_in_stock && product?.stock?.qty > 0) || true;
  const pricing = get_product_price({ product, is_MRP: true });
  return gtmEvent(
    'buy_now_clicked',
    {
      cart_id,
      product_id: product?.entity_id,
      ...get_product_category_sub_category({ product }),
      ...get_product_brand({ product }),
      ...get_product_sku({ product }),
      ...get_product_image({ product }),
      product_name: Array.isArray(product?.name)
        ? product?.name?.[0]
        : product?.name,
      ...pricing,
      total_product_price: pricing?.listed_price,
      stock_availability: is_stock,
      quantity: 1,
      currency: 'SAR',
      source: 'web',
      bundle_deal_applied: product?.type_id == 'bundle',
      view_type: 'product-detail-page',
    },
    null,
    true,
    false
  );
};

// Update cart Item
export const event_update_cart = ({ cartData }) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    'cart_updated',
    {
      cart_id,
      sub_total_cart_value: cartData?.subtotal,
      shipping_and_handling_fee: cartData?.shipping_amount,
      tax_fee: cartData?.tax_amount,
      total_cart_value: cartData?.grand_total,
      total_products: cartData?.items?.length || 0,
      ...(cartData?.coupon_code && { coupon_discount: cartData?.coupon_code }),
      items: cartData?.items?.map((product) => {
        return {
          product_id: product?.entity_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
    },
    null,
    true,
    false
  );
};

// Event View Cart
// Every line of a cart or order in the spec's item shape, each carrying its own
// list attribution — a cart assembled from three lists reports three origins.
//
// Deliberately NOT `getLineItems`, which truncates to 12 items (§2) and would
// silently drop the rest of a large cart.
export const buildCartItems = ({ cartLines, locale, unavailableSkus }) =>
  (cartLines || [])
    .map((product, index) => {
      const item_id = getItemId(product);
      return buildListItem({
        product,
        index,
        locale,
        unavailableSkus,
        list: item_id ? getItemAttribution(item_id) : undefined,
        quantity: product?.qty || product?.qty_ordered || 1,
      });
    })
    .filter(Boolean);

// The sum of the lines' own `price × quantity`: `value` on the cart-change
// events, and the fallback for `getCheckoutValue`.
export const getCartValue = (items) =>
  round2(
    (items || []).reduce(
      (total, item) => total + (item?.price || 0) * (item?.quantity || 1),
      0
    )
  );

const getLinesKey = (lines) =>
  lines
    .map(({ id, qty }) => `${id}x${Number(qty) || 1}`)
    .sort()
    .join('|');

export const getCheckoutValue = (cart, items) => {
  const total = Number(
    cart?.total_segments?.find((segment) => segment?.code === 'grand_total')
      ?.value ?? cart?.grand_total
  );
  const sameLines =
    getLinesKey(
      (cart?.items || []).map((line) => ({
        id: getItemId(line),
        qty: line?.qty,
      }))
    ) ===
    getLinesKey(
      items.map((item) => ({ id: item.item_id, qty: item.quantity }))
    );

  return Number.isFinite(total) && sameLines
    ? round2(total)
    : getCartValue(items);
};

// Fired once per /cart page view, when the page has cart data. A quantity change
// re-renders the cart but must NOT re-fire — the guard lives in `useViewCart`,
// whose ref is scoped to the page component, so leaving /cart and coming back is
// a new view and reports again.
//
// Opening the header mini-cart does not fire this; nothing there calls it.
//
// This carries none of the legacy payload (`cart_id`, `sub_total_cart_value`,
// `shipping_and_handling_fee`, `tax_fee`, `total_cart_value`,
// `total_products`, `coupon_discount`). The legacy `cart_viewed` still fires
// beside it from `useViewCart` — see the legacy block at the end of this file.
//
// `event_id` is in the spec sample but out of scope. `value` is what the
// shopper will pay, the cart's grand total — see `getCheckoutValue`.
export const event_view_cart = ({ cartData, locale, unavailableSkus }) => {
  const items = buildCartItems({
    cartLines: cartData?.items,
    locale,
    unavailableSkus,
  });
  if (!items.length) return;

  return gtmEventNow(
    'view_cart',
    { currency: 'SAR', value: getCheckoutValue(cartData, items), items },
    null,
    true,
    false
  );
};

// Fired on the **click** of Complete Purchase — cart page, cart pop-up, header
// mini-cart, and the PDP checkout button — and on PDP Buy Now, which pushes
// this straight after its `add_to_cart`.
//
// The click is the checkout attempt, so this lands *before* any redirect, guest
// sign-in included. It is pushed synchronously for that reason.
//
// The legacy `checkout_started` fired on the /checkout render — the moment the
// spec says this event must not. It still fires there, unchanged; this one
// fires on the click instead, so the two describe different moments. Firing on
// the click means a guest who signs in and lands on /checkout does not report a
// second `begin_checkout`, while returning to the cart and pressing the button
// again correctly does.
//
// Event-level `coupon` is the cart's coupon code; the item-level `coupon` is the
// product own offer label, and both are dropped when absent rather than sent
// empty. `event_id` is in the spec sample but out of scope.
export const event_begin_checkout = ({
  cartLines,
  cart,
  coupon,
  locale,
  unavailableSkus,
}) => {
  const items = buildCartItems({ cartLines, locale, unavailableSkus });
  if (!items.length) return;

  const coupon_code = clean(coupon);

  return gtmEventNow(
    'begin_checkout',
    {
      currency: 'SAR',
      value: getCheckoutValue(cart, items),
      ...(coupon_code && { coupon: coupon_code }),
      items,
    },
    null,
    true,
    false
  );
};

// Fired once per checkout, when /checkout renders with the shipping selection
// the shopper already confirmed — see `useAddShippingInfo`.
//
// This checkout has no shipping-tier step of its own: `RedirectToCheckout` will
// not let the shopper reach /checkout until the tier and its address or branch
// are set in the header modal, so the spec carve-out applies — "if /checkout has
// no separate shipping step … fire once per checkout when the checkout page
// renders with that confirmed selection". The header modal itself never fires
// it; it only feeds the `shipping_method` global variable, which is the same
// store this reads.
//
// `branch_id` / `branch_name` are Store Pickup only. On Home Delivery there is no
// branch and both are dropped rather than sent empty (spec §2).
//
// `event_id` is in the spec sample but out of scope. `value` is the cart's grand
// total — see `getCheckoutValue`.
export const event_add_shipping_info = ({
  cart,
  shippingTier,
  branch,
  locale,
  unavailableSkus,
}) => {
  const shipping_tier = clean(shippingTier);
  if (!shipping_tier) return;

  const items = buildCartItems({
    cartLines: cart?.items,
    locale,
    unavailableSkus,
  });
  if (!items.length) return;

  // Branches carry a human code (`source_code` / `code`) alongside a numeric id;
  // the spec sample shows the code.
  const branch_id = clean(
    branch?.source_code || branch?.code || branch?.location_id || branch?.id
  );
  const branch_name = clean(branch?.name || branch?.firstname);
  const coupon_code = clean(cart?.coupon_code);

  return gtmEventNow(
    'add_shipping_info',
    {
      currency: 'SAR',
      value: getCheckoutValue(cart, items),
      shipping_tier,
      ...(branch_id && { branch_id }),
      ...(branch_name && { branch_name }),
      ...(coupon_code && { coupon: coupon_code }),
      items,
    },
    null,
    true,
    false
  );
};

export const event_payment_initiated = ({
  cartData,
  payment_mode,
  totals = [],
}) => {
  const cart_id = getCookie('quote');
  const wallet = totals?.find(
    (total) =>
      total?.code === 'amstorecredit' &&
      (total?.title === 'Store Credit' || total?.title === 'المحفظة')
  );
  const rajhi_points = totals?.find((i) => i.code === 'alrajhi_points');
  const qitaf_points = totals?.find((i) => i.code === 'stc_qitaf');
  return gtmEvent(
    'payment_initiated',
    {
      cart_id,
      sub_total_cart_value: cartData?.subtotal,
      shipping_and_handling_fee: cartData?.shipping_amount,
      tax_fee: cartData?.tax_amount,
      total_cart_value: cartData?.grand_total,
      total_products: cartData?.items?.length || 0,
      ...(cartData?.coupon_code && { coupon_discount: cartData?.coupon_code }),
      ...(wallet && { wallet_applied: true, wallet_points: wallet?.value }),
      ...(rajhi_points && {
        rajhi_reward: true,
        rajhi_reward_points: rajhi_points?.value,
      }),
      ...(qitaf_points && {
        qitaf_reward: true,
        qitaf_points: qitaf_points?.value,
      }),
      items: cartData?.items?.map((product) => {
        return {
          product_id: product?.entity_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
      payment_mode,
    },
    null,
    true,
    false
  );
};

// Event For Success Page
export const event_checkout_completed = ({ order }) => {
  gtmEvent(
    'checkout_completed',
    {
      cart_id: order?.quote_id,
      transaction_id: order?.increment_id,
      order_id: order?.entity_id,
      sub_total_cart_value: order?.subtotal,
      tax_fee: order?.tax_amount,
      total_cart_value: order?.grand_total,
      ...(order?.coupon_code && { coupon_code: order?.coupon_code }),
      ...(order?.discount_amount && {
        coupon_discount: order?.discount_amount,
      }),
      total_products: order?.items?.length,
      payment_mode: order?.payment?.method,
      ...(order?.extension_attributes?.alrajhi_points_amount && {
        rajhi_reward: true,
        rajhi_reward_points: order?.extension_attributes?.alrajhi_points_amount,
      }),
      ...(order?.extension_attributes?.stc_qitaf_amount && {
        qitaf_reward: true,
        qitaf_points: order?.extension_attributes?.stc_qitaf_amount,
      }),
      payment_completed: order?.status === 'complete',
      items: order?.items?.map((product) => {
        return {
          product_id: product?.product_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
    },
    null,
    true,
    false
  );
  return gtmEvent(
    'payment_success',
    {
      cart_id: order?.quote_id,
      transaction_id: order?.increment_id,
      order_id: order?.entity_id,
      sub_total_cart_value: order?.subtotal,
      tax_fee: order?.tax_amount,
      total_cart_value: order?.grand_total,
      ...(order?.coupon_code && { coupon_code: order?.coupon_code }),
      ...(order?.discount_amount && {
        coupon_discount: order?.discount_amount,
      }),
      total_products: order?.items?.length,
      payment_mode: order?.payment?.method,
      ...(order?.extension_attributes?.alrajhi_points_amount && {
        rajhi_reward: true,
        rajhi_reward_points: order?.extension_attributes?.alrajhi_points_amount,
      }),
      ...(order?.extension_attributes?.stc_qitaf_amount && {
        qitaf_reward: true,
        qitaf_points: order?.extension_attributes?.stc_qitaf_amount,
      }),
      payment_completed: order?.status === 'complete',
      items: order?.items?.map((product) => {
        return {
          product_id: product?.product_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
    },
    null,
    true,
    false
  );
};

// used in cart page
export const event_coupon_code = ({
  grand_total,
  coupon_code,
  coupon_discount,
  event_name,
}) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    event_name,
    {
      cart_id,
      coupon_code,
      coupon_discount,
      cart_value_after_discount: grand_total - Math.abs(coupon_discount || 0),
      cart_value_before_discount: grand_total,
    },
    null,
    true,
    false
  );
};

export const event_update_address = ({ region, district, street }) => {
  return gtmEvent(
    'address_updated',
    {
      source: 'web',
      region,
      district,
      street,
    },
    null,
    true,
    false
  );
};

// Event Button Click
export const event_button_click = ({
  button_name,
  button_destination,
  button_location,
}) => {
  return gtmEvent(
    'button_click',
    {
      button_name,
      ...(button_destination && { button_destination }),
      button_location,
    },
    null,
    false,
    false
  );
};

// Event Purchase
export const event_purchase = ({ order, locale }) => {
  const transaction_id = order?.increment_id;
  if (!transaction_id || wasPurchasePushed(transaction_id)) return;

  const items = buildCartItems({ cartLines: order?.items, locale });
  const grandTotal = Number(order?.grand_total);
  const value = Number.isFinite(grandTotal)
    ? round2(grandTotal)
    : getCartValue(items);
  const shipping_tier = getShippingTier(
    order?.extension_attributes?.shipping_assignments?.[0]?.shipping?.method
  );

  markPurchasePushed(transaction_id);

  return gtmEvent(
    'purchase',
    {
      currency: 'SAR',
      value,
      ...(order?.coupon_code && { coupon: order?.coupon_code }),
      transaction_id: String(transaction_id),
      tax: Number(order?.tax_amount || 0),
      shipping: Number(order?.shipping_amount || 0),
      ...(order?.payment?.method && {
        payment_type: getPaymentType(order?.payment?.method),
      }),
      ...(shipping_tier && { shipping_tier }),
      items,
    },
    null,
    true,
    false
  );
};

// Event Add Payment Info
// `value` is the cart's grand total — see `getCheckoutValue`.
export const event_add_payment_info = ({ cartData, payment_type, locale }) => {
  const items = buildCartItems({ cartLines: cartData?.items, locale });
  return gtmEvent(
    'add_payment_info',
    {
      currency: 'SAR',
      value: getCheckoutValue(cartData, items),
      ...(cartData?.coupon_code && { coupon: cartData?.coupon_code }),
      payment_type: getPaymentType(payment_type),
      items,
    },
    null,
    true,
    false
  );
};

// Event Filters Used
export const event_filters_used = ({
  filter_name,
  filter_value,
  filter_value_id,
  filter_action,
}) => {
  const item_list_id = getItemListId();
  return gtmEvent(
    'filters_used',
    {
      filter_name,
      ...(filter_value && { filter_value: String(filter_value) }),
      ...(filter_value_id && { filter_value_id: String(filter_value_id) }),
      filter_action,
      ...(item_list_id && { item_list_id }),
    },
    null,
    false,
    false
  );
};

// Event Add Shipping Address
export const event_add_shipping_address = ({
  user_id,
  city,
  district,
  address,
}) => {
  const referral_url =
    typeof window === 'object' ? window.location.href : undefined;
  return gtmEvent(
    'add_shipping_address',
    {
      ...(user_id && { user_id: String(user_id) }),
      country: 'Saudi Arabia',
      ...(city && { city }),
      ...(district && { district }),
      ...(address && { address }),
      ...(referral_url && { referral_url }),
    },
    null,
    false,
    false
  );
};

// Event OTP Send
const OTP_SENT_AT_KEY = 'gtm_otp_sent_at';

// DAAM v3: `otp_send` reports the send request's round-trip,
// `otp_verification` the time since that OTP was sent.
export const event_otp_send = async (sendOtpRequest) => {
  const startedAt = Date.now();
  const res = await sendOtpRequest();

  if (res?.data && res?.error?.success !== 'false') {
    const referral_url = getCallbackReferralUrl();
    try {
      sessionStorage.setItem(OTP_SENT_AT_KEY, String(Date.now()));
    } catch {}
    gtmEvent(
      'otp_send',
      {
        ...(referral_url && { referral_url }),
        otp_channel: 'SMS',
        otp_latency_ms: Date.now() - startedAt,
      },
      null,
      false,
      false
    );
  }

  return res;
};

// Event OTP Verification
export const event_otp_verification = ({ otp_result }) => {
  const referral_url = getCallbackReferralUrl();
  let sentAt = null;
  try {
    sentAt = Number(sessionStorage.getItem(OTP_SENT_AT_KEY)) || null;
  } catch {}
  return gtmEvent(
    'otp_verification',
    {
      otp_result,
      ...(referral_url && { referral_url }),
      ...(sentAt && { otp_latency_ms: Date.now() - sentAt }),
    },
    null,
    false,
    false
  );
};

// Event Coupon Redemption
export const event_coupon_redemption = ({
  code,
  redemption_result,
  redemption_location,
}) => {
  const trimmed = code?.trim();
  if (!trimmed) return;
  return gtmEvent(
    'coupon_redemption',
    {
      code: trimmed,
      redemption_result,
      redemption_location,
    },
    null,
    false,
    false
  );
};

export const event_coupon_code_failed = ({
  grand_total,
  coupon_code,
  reason,
}) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    'coupon_code_failed',
    {
      cart_id,
      coupon_code,
      reason,
      cart_value_after_discount: grand_total,
      cart_value_before_discount: grand_total,
    },
    null,
    true,
    false
  );
};

export const event_address_option_selected = ({ value }) => {
  return gtmEvent(
    'address_option_selected',
    {
      source: 'web',
      value,
    },
    null,
    true,
    false
  );
};

// Fired once per list, carrying every item that has been at least 50% visible
// for 2 seconds — see `useItemListImpression`. The batcher in
// `item-list-impressions.js` collects the qualified items so one push describes
// the whole list, in list order, rather than one push per card.
//
// `list` is `{ item_list_id, item_list_name }`; `items` come from
// `buildListItem`, already in the spec's item shape with the list identity
// repeated on each.
export const event_view_item_list = (list, items) => {
  if (!list?.item_list_id || !items?.length) return;
  // As with the promotion events, the spec's `ecommerce` object has no
  // `page_path`, so it is suppressed here.
  return gtmEvent(
    'view_item_list',
    { currency: 'SAR', ...list, items },
    null,
    true,
    false
  );
};

// Fired on a click that opens a PDP from any list — see `useSelectItem`, which
// is attached to the image and the name only, so Add To Cart, the wishlist heart
// and the brand link do not report a selection.
//
// Exactly one item per push: the clicked product, carrying the same identifiers
// its `view_item_list` impression reported, with `index` its zero-based position
// in that list.
//
// Pushed synchronously via `gtmEventNow` because the click navigates away, and a
// deferred push would not survive the unload.
//
// `event_id` is in the spec sample but out of scope for this project and is not
// sent on any event.
export const event_select_item = (list, item) => {
  if (!list?.item_list_id || !item) return;
  // As with the promotion events, the spec's `ecommerce` object has no
  // `page_path`, so it is suppressed here.
  return gtmEventNow(
    'select_item',
    { currency: 'SAR', items: [item] },
    null,
    true,
    false
  );
};

// Fired for the creatives that qualified together, each having been at least 50%
// inside the viewport for 2 seconds — see `usePromotionImpression`, which counts
// the impressions, and `promotion-impressions.js`, which batches them. Entries
// come from `buildPromotion`, already in the spec's item shape.
export const event_view_promotion = (promotions) => {
  const items = (Array.isArray(promotions) ? promotions : [promotions]).filter(
    Boolean
  );
  if (!items.length) return;

  // The spec's `ecommerce` object holds `items` and nothing else, so the
  // non-spec `page_path` is suppressed for the promotion events.
  // Synchronous: the buffer is flushed as the shopper navigates away, and a
  // deferred push would not survive the unload.
  return gtmEventNow('view_promotion', { items }, null, true, false);
};

// Fired on a real click of the same creative, carrying the identifiers its
// impression reported so GA4 can join the two. `destination` is the absolute
// landing URL, already resolved by `fireSelectPromotion`.
//
// Pushed synchronously via `gtmEventNow`: the click may open a new tab or leave
// for an external banner, and the spec requires the event to land before the
// navigation. A deferred push would be lost on a full page unload.
export const event_select_promotion = (promotion, destination) => {
  if (!promotion) return;
  return gtmEventNow(
    'select_promotion',
    {
      items: [
        {
          ...promotion,
          ...(destination && { promotion_destination: destination }),
        },
      ],
    },
    null,
    true,
    false
  );
};

const scrubPersonalData = (message) =>
  message
    .replace(/[^\s@:]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .replace(/(?:\+?966|\b0)?5\d{8}\b/g, '[phone]');

const pushErrorEvent = ({
  error_message,
  error_code,
  error_type,
  fallback_code,
}) => {
  if (typeof window !== 'object') return;
  const message = scrubPersonalData(String(error_message || '').trim());
  if (!message) return;

  const failure = takeRecentApiFailure();
  const code = error_code || failure?.code || fallback_code;
  const type =
    !error_code && failure && error_type === 'UI Error'
      ? 'API Error'
      : error_type;

  return gtmEvent(
    'errors',
    {
      error_message: message.slice(0, 100),
      ...(code && { error_code: String(code) }),
      error_type: type,
      ...getErrorContext(),
    },
    null,
    false,
    false
  );
};

// used in error boundry event
export const event_errors = (payload) => {
  noteExplicitError();
  return pushErrorEvent(payload);
};

// An error shown under a form field (validation, or an API error a handler put
// there with `setFieldError`) — see `useReportFieldError`. Held back like a
// toast, so a screen that already called `event_errors` is not reported twice.
// A validation error has no API code, so it gets `VALIDATION`.
export const event_field_error = ({ message }) =>
  reportUnlessExplicit(message, () =>
    pushErrorEvent({
      error_message: message,
      error_type: 'UI Error',
      fallback_code: 'VALIDATION',
    })
  );

// Every `toast.error` in the app arrives here through `@/utils/toast`. It stands
// down when the same failure was already reported by hand — see
// `error-suppression.js`.
export const event_toast_error = (payload) =>
  reportUnlessExplicit(payload?.error_message, () => pushErrorEvent(payload));
export const event_login = ({
  user,
  method = 'Mobile OTP',
  status = 'Success',
  referral_url,
}) => {
  return gtmEvent(
    'login',
    {
      ...getAuthPayload({ user, method, status, referral_url }),
      ...getPurchaseStats(user),
    },
    null,
    false,
    false
  );
};

export const event_sign_up = ({
  user,
  method = 'Mobile OTP',
  status = 'Success',
  referral_url,
}) => {
  return gtmEvent(
    'sign_up',
    getAuthPayload({ user, method, status, referral_url }),
    null,
    false,
    false
  );
};

// Logout Event
// Login, Logout, Register Event
export const event_user_auth = (
  user,
  event_name,
  method,
  userType,
  userStatus
) => {
  const { firstname, lastname, email, id, mobilenumber, created_at } =
    user || {};
  const userData = {
    first_name: firstname,
    last_name: lastname,
    ...(id && { user_id: String(id) }),
    ...(email ? { user_email: crypto.SHA256(email).toString() } : {}),
    ...(mobilenumber
      ? {
          user_phone: crypto
            .SHA256(`966`.concat(mobilenumber?.slice(-9)))
            .toString(),
        }
      : {}),
    ...(userType && { user_type: userType }),
    ...(method !== 'register' && { method }),
    status:
      userStatus ||
      (method === 'register'
        ? 'User Registered Successfully'
        : 'User LoggedIn Successfully'),
    referral_url: window.location.origin,
    event_source: 'web',
    account_creation_date: created_at
      ? convertDate(created_at)
      : convertDate(new Date().toISOString()),
  };

  return gtmEvent(event_name, userData, null, false);
};

export const event_user_register = (user_id, locale) => {
  const userData = {
    ...(user_id && { user_user_id: String(user_id) }),
    language: locale,
    source: 'web',
    register_source: 'account_page',
  };

  return gtmEvent('register', userData, null, false);
};

export const event_delete_account = async () => {
  const session = await getSharedSession();
  return gtmEvent(
    'account_deleted_successfully',
    {
      values: [{ ...session?.user }],
    },
    null,
    true,
    false
  );
};

export const event_prescription = () => {
  return gtmEvent('prescription', {
    page_type: 'prescription',
  });
};

// for each banner click
export const event_banner_click = async ({
  banner_id,
  banner_name,
  banner_category,
  section,
}) => {
  const session = await getSharedSession();
  return gtmEvent(
    'banner_clicked',
    {
      banner_id,
      banner_name,
      banner_category,
      section,
      source: 'web',
      user_type: session?.user ? 'logged-in' : 'guest',
    },
    null,
    true,
    false
  );
};

export const event_new_address = () => {
  return gtmEvent(
    'add_new_address_click',
    {
      source: 'web',
    },
    null,
    true,
    false
  );
};

export const event_selected_store = ({ branch_name }) => {
  return gtmEvent(
    'store_selected',
    {
      source: 'web',
      branch_name,
    },
    null,
    true,
    false
  );
};

export const event_change_lang = ({ locale }) => {
  return gtmEvent(
    'language_selected',
    {
      source: 'web',
      value: locale,
    },
    null,
    true,
    false
  );
};

export const event_view_wishlist = () => {
  return gtmEvent(
    'favourite_viewed',
    {
      source: 'web',
    },
    null,
    true,
    false
  );
};

export const event_top_nav_clicked = ({ menu_name }) => {
  return gtmEvent(
    'top_navigation_bar_clicked',
    {
      source: 'web',
      menu_name,
    },
    null,
    true,
    false
  );
};

export const event_user_profile_menu_clicked = ({ menu_name }) => {
  return gtmEvent(
    'profile_menu_clicked',
    {
      source: 'web',
      menu_name,
    },
    null,
    true,
    false
  );
};

export const event_profile_updated = async ({ label }) => {
  const session = await getSharedSession();

  return gtmEvent(
    'profile_updated',
    {
      label,
      values: [{ ...session?.user }],
    },
    null,
    true,
    false
  );
};

export const event_submit_prescription = ({
  prescription_type,
  prescription_image,
  prescription_details,
  national_id,
  prescription_number,
  insurance_co_name,
  insurance_acceptance_number,
  payment_method,
  product_unavailable_action,
}) => {
  return gtmEvent(
    'prescription_request_sent',
    {
      ...(prescription_type && { prescription_type }),
      ...(prescription_image && { prescription_image }),
      ...(prescription_details && { prescription_details }),
      ...(national_id && { national_id }),
      ...(prescription_number && { prescription_number }),
      ...(insurance_co_name && { insurance_co_name }),
      ...(insurance_acceptance_number && { insurance_acceptance_number }),
      ...(payment_method && { payment_method }),
      ...(product_unavailable_action && { product_unavailable_action }),
    },
    null,
    true,
    false
  );
};

export const event_view_prescription = ({ prescriptions = [] }) => {
  return gtmEvent(
    'prescription_viewed',
    {
      items: prescriptions?.map((prescription) => {
        return {
          ...(prescription?.pharmacist_data?.id && {
            prescription_number: prescription?.pharmacist_data?.id,
          }),
          ...(prescription?.prescription_type && {
            prescription_type: prescription?.prescription_type,
          }),
          ...(prescription?.date && { date: convertDate(prescription?.date) }),
          ...(prescription?.status && { status: prescription?.status }),
          ...(prescription?.customer_data?.name && {
            customer_name: prescription?.customer_data?.name,
          }),
          ...(prescription?.customer_data?.phone && {
            phone: prescription?.customer_data?.phone,
          }),
          ...(prescription?.insurance_co_name && {
            insurane_company_name: prescription?.insurance_co_name,
          }),
          ...(prescription?.insurance_acceptance_number && {
            insurance_acceptance_number:
              prescription?.insurance_acceptance_number,
          }),
          ...(prescription?.delivery_method && {
            delivery_method: prescription?.delivery_method,
          }),
          ...(prescription?.address && {
            delivery_address: prescription?.address,
          }),
          ...(prescription?.product_unavailable_action && {
            product_unavailability: prescription?.product_unavailable_action,
          }),
        };
      }),
    },
    null,
    true,
    false
  );
};

export const event_contact_us = async ({ name, email, details }) => {
  const session = await getSharedSession();
  return gtmEvent(
    'contact_us_request_send',
    {
      values: [
        {
          name,
          email,
          details,
          ...(session?.user && { phone: session?.user?.mobilenumber }),
        },
      ],
    },
    null,
    true,
    false
  );
};

export const event_view_brand = ({ brand_name, brand_category }) => {
  return gtmEvent(
    'brand_viewed',
    {
      brand_name,
      brand_category,
      source: 'web',
      view_type: getCookie('brand_view_type') || 'home-page',
    },
    null,
    true,
    false
  );
};

export const event_view_category = ({
  parent_category_id,
  parent_category_name,
  is_offer = false,
}) => {
  return gtmEvent(
    'category_viewed',
    {
      ...(parent_category_id && !is_offer && { parent_category_id }),
      ...(parent_category_name && !is_offer && { parent_category_name }),
      ...(parent_category_name &&
        is_offer && { offer_name: parent_category_name }),
      source: 'web',
    },
    null,
    true,
    false
  );
};

export const event_view_sub_category = ({
  parent_category_id,
  parent_category_name,
  sub_category_id,
  sub_category_name,
}) => {
  return gtmEvent(
    'sub_category_viewed',
    {
      ...(parent_category_id && { parent_category_id }),
      ...(parent_category_name && { parent_category_name }),
      source: 'web',
      sub_category_id,
      sub_category_name,
    },
    null,
    true,
    false
  );
};

export const event_filter_applied = ({ filter_type = null }) => {
  return gtmEvent(
    'filter_applied',
    {
      source: 'web',
      filter_type,
    },
    null,
    true,
    false
  );
};

export const event_installment_option = ({ merchant_name }) => {
  return gtmEvent(
    'installment_option_explored',
    {
      source: 'web',
      merchant_name,
    },
    null,
    true,
    false
  );
};

export const event_alrajhi_reward_applied = ({ phone, amount }) => {
  return gtmEvent(
    'alrajhi_reward_applied',
    {
      phone,
      amount,
      currency: 'SAR',
    },
    null,
    true,
    false
  );
};

export const event_qitaf_points_applied = ({ phone, amount }) => {
  return gtmEvent(
    'qitaf_points_applied',
    {
      phone,
      amount,
      currency: 'SAR',
    },
    null,
    true,
    false
  );
};

// ---------------------------------------------------------------------------
// Legacy events, kept firing alongside their spec replacements
//
// These are the original implementations, recovered verbatim from `ee50d766`
// (the commit this branch started from). Each pushes a dataLayer event name that
// the spec rebuild would otherwise have retired, so nothing that reads the old
// container breaks while Daam migrates.
//
// `product_quantity_increased` and `product_quantity_decreased` are pinned to the
// stepper branch on purpose: their originals also pushed `add_to_cart` and
// `remove_from_cart` under those exact names, and firing those again would
// double-count the spec events. Only the names that actually stopped firing are
// restored.
//
// Do not extend these. New parameters belong on the spec events.
// ---------------------------------------------------------------------------

export const event_legacy_product_viewed = ({ product }) => {
  const is_stock =
    (product?.stock?.is_in_stock && product?.stock?.qty > 0) || true;
  const view_type = getCookie('product_view_type');
  return gtmEvent(
    'product_viewed',
    {
      ...get_product_category_sub_category({ product }),
      ...get_product_brand({ product }),
      ...get_product_sku({ product }),
      ...get_product_price({ product, is_MRP: true }),
      ...get_product_image({ product }),
      product_id: product?.entity_id,
      product_name: Array.isArray(product?.name)
        ? product?.name?.[0]
        : product?.name,
      stock_availability: is_stock,
      currency: 'SAR',
      source: 'web',
      view_type:
        view_type === 'undefined' || view_type === undefined
          ? 'product-detail-page'
          : view_type,
    },
    null,
    true,
    false
  );
};

export const event_legacy_quantity_increased = ({
  product,
  qty,
  is_plus,
  total_cart_value,
  atc_type,
  bundle_deal_applied,
}) => {
  const cart_id = getCookie('quote');
  const is_stock =
    (product?.stock?.is_in_stock && product?.stock?.qty > 0) || true;
  const pricing = get_product_price({ product, is_MRP: true });
  return gtmEvent(
    'product_quantity_increased',
    {
      cart_id,
      ...get_product_category_sub_category({ product }),
      ...get_product_brand({ product }),
      ...get_product_sku({ product }),
      ...get_product_image({ product }),
      product_id: product?.entity_id || product?.item_id,
      product_name: Array.isArray(product?.name)
        ? product?.name?.[0]
        : product?.name,
      ...pricing,
      total_product_price: pricing?.listed_price * qty,
      stock_availability: is_stock,
      quantity: qty,
      currency: 'SAR',
      source: 'web',
      ...(is_plus && {
        total_cart_value: total_cart_value
          ? total_cart_value + pricing?.listed_price
          : pricing?.listed_price * qty,
      }),
      ...(atc_type && { atc_type }),
      ...(typeof bundle_deal_applied === 'boolean' && { bundle_deal_applied }),
    },
    null,
    true,
    false
  );
};

export const event_legacy_quantity_decreased = ({
  product,
  is_minus = false,
  total_cart_value,
  bundle_deal_applied,
  view_type,
}) => {
  const cart_id = getCookie('quote');
  const is_stock =
    (product?.stock?.is_in_stock && product?.stock?.qty > 0) || true;
  const pricing = get_product_price({ product, is_MRP: true });
  return gtmEvent(
    'product_quantity_decreased',
    {
      cart_id,
      ...get_product_category_sub_category({ product }),
      ...get_product_brand({ product }),
      ...get_product_sku({ product }),
      ...get_product_image({ product }),
      product_id: product?.entity_id || product?.item_id,
      product_name: Array.isArray(product?.name)
        ? product?.name?.[0]
        : product?.name,
      ...pricing,
      total_product_price: pricing?.listed_price * (product?.qty || 1),
      stock_availability: is_stock,
      quantity: product?.qty || 1,
      currency: 'SAR',
      source: 'web',
      ...(is_minus && {
        total_cart_value: total_cart_value
          ? total_cart_value - pricing?.listed_price
          : pricing?.listed_price * (product?.qty || 1),
      }),
      view_type,
      ...(typeof bundle_deal_applied === 'boolean' && { bundle_deal_applied }),
    },
    null,
    true,
    false
  );
};

export const event_legacy_cart_viewed = ({ cartData }) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    'cart_viewed',
    {
      cart_id,
      sub_total_cart_value: cartData?.subtotal,
      shipping_and_handling_fee: cartData?.shipping_amount,
      tax_fee: cartData?.tax_amount,
      total_cart_value: cartData?.grand_total,
      total_products: cartData?.items?.length || 0,
      ...(cartData?.coupon_code && { coupon_discount: cartData?.coupon_code }),
      items: cartData?.items?.map((product) => {
        return {
          product_id: product?.entity_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
    },
    null,
    true,
    false
  );
};

export const event_legacy_checkout_started = ({ cartData }) => {
  const cart_id = getCookie('quote');
  return gtmEvent(
    'checkout_started',
    {
      cart_id,
      sub_total_cart_value: cartData?.subtotal,
      shipping_and_handling_fee: cartData?.shipping_amount,
      tax_fee: cartData?.tax_amount,
      total_cart_value: cartData?.grand_total,
      total_products: cartData?.items?.length || 0,
      ...(cartData?.coupon_code && { coupon_discount: cartData?.coupon_code }),
      items: cartData?.items?.map((product) => {
        return {
          product_id: product?.entity_id || product?.item_id,
          quantity: product?.qty || 1,
          product_name: Array.isArray(product?.name)
            ? product?.name?.[0]
            : product?.name,
          ...get_product_category_sub_category({ product }),
          ...get_product_brand({ product }),
          ...get_product_price({ product }),
          ...get_product_image({ product }),
        };
      }),
    },
    null,
    true,
    false
  );
};
